import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ProtectedRoute, defaultPath } from "@/components/ProtectedRoute";
import { ToastViewport } from "@/components/Toast";
import { OfflineBanner } from "@/components/OfflineBanner";
import { OPERATIONAL_ROLES } from "@/lib/roles";
import Login from "@/pages/Login";
import AdminLayout from "@/components/AdminLayout";
import SupervisorLayout from "@/components/SupervisorLayout";
import MobileLayout from "@/components/MobileLayout";

import AdminDashboard from "@/pages/admin/Dashboard";
import AdminClients from "@/pages/admin/Clients";
import ClientDetail from "@/pages/admin/ClientDetail";
import AdminUsers from "@/pages/admin/Users";
import AdminPatrols from "@/pages/admin/Patrols";
import PatrolDetail from "@/pages/admin/PatrolDetail";
import LiveTrackingMap from "@/pages/admin/LiveTrackingMap";
import AdminNews from "@/pages/admin/News";
import AdminNewNews from "@/pages/admin/NewNews";
import AdminRequests from "@/pages/admin/Requests";
import AdminNewRequest from "@/pages/admin/NewRequest";
import AdminServices from "@/pages/admin/Services";
import AdminVehicles from "@/pages/admin/Vehicles";
import AdminPanic from "@/pages/admin/Panic";

import SupervisorDashboard from "@/pages/supervisor/Dashboard";
import SupervisorHomeMobile from "@/pages/supervisor/Home";
import SupervisorNewNews from "@/pages/supervisor/NewNews";
import SupervisorService from "@/pages/supervisor/Services";
import SupervisorCheckin from "@/pages/supervisor/Checkin";
import SupervisorRequests from "@/pages/supervisor/Requests";
import SupervisorServicesPanel from "@/pages/supervisor/ServicesPanel";
import Vehicles from "@/pages/shared/Vehicles";
import Materials from "@/pages/shared/Materials";
import Visits from "@/pages/shared/Visits";
import AuthorizedPersons from "@/pages/shared/AuthorizedPersons";
import Training from "@/pages/shared/Training";
import WorkItems from "@/pages/shared/WorkItems";
import Logistics from "@/pages/shared/Logistics";
import Phones from "@/pages/shared/Phones";
import PassiveGuards from "@/pages/shared/PassiveGuards";
import Schedule from "@/pages/shared/Schedule";
import WorkHours from "@/pages/shared/WorkHours";
import SupervisorRoutes from "@/pages/shared/SupervisorRoutes";
import Quizzes from "@/pages/shared/Quizzes";
import ChecklistServicio from "@/pages/shared/ChecklistServicio";
import CheckinsLog from "@/pages/shared/CheckinsLog";
import ClienteLayout from "@/components/ClienteLayout";
import ClientePortal from "@/pages/cliente/ClientePortal";
import QuizGate from "@/components/QuizGate";
import ServiceChecklistGate from "@/components/ServiceChecklistGate";
import BrandManual from "@/pages/shared/Brand";
import Migration from "@/pages/admin/Migration";
import Budget from "@/pages/admin/budget/Budget";
import ChatsPage from "@/pages/shared/Chats";
import OpsTasksAnalytics from "@/pages/shared/OpsTasksAnalytics";
import Profile from "@/pages/shared/Profile";

import VigiladorHome from "@/pages/vigilador/Home";
import VigiladorPatrolActive from "@/pages/vigilador/PatrolActive";
import VigiladorPatrolView from "@/pages/vigilador/PatrolView";
import VigiladorNewNews from "@/pages/vigilador/NewNews";
import VigiladorRequests from "@/pages/vigilador/Requests";
import VigiladorOpsTasks from "@/pages/vigilador/OpsTasks";

import VerifyPatrol, { VerifyPeriod } from "@/pages/public/Verify";

function RootRedirect() {
  const { user, loading } = useAuth();
  if (loading || user === null) return null;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={defaultPath(user.role)} replace />;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route path="/" element={<RootRedirect />} />
            <Route path="/login" element={<Login />} />
            <Route path="/verify/patrol/:token" element={<VerifyPatrol />} />
            <Route path="/verify/period/:token" element={<VerifyPeriod />} />

            {/* Admin */}
            <Route path="/admin" element={<ProtectedRoute roles={["admin"]}><AdminLayout /></ProtectedRoute>}>
              <Route index element={<AdminDashboard />} />
              <Route path="clientes" element={<AdminClients />} />
              <Route path="clientes/:id" element={<ClientDetail />} />
              <Route path="usuarios" element={<AdminUsers />} />
              <Route path="rondas" element={<AdminPatrols />} />
              <Route path="rondas/:id" element={<PatrolDetail />} />
              <Route path="rondas-vivo" element={<LiveTrackingMap />} />
              <Route path="novedades" element={<AdminNews />} />
              <Route path="novedad" element={<AdminNewNews />} />
              <Route path="solicitudes" element={<AdminRequests />} />
              <Route path="solicitud" element={<AdminNewRequest />} />
              <Route path="servicios" element={<AdminServices />} />
              <Route path="chats" element={<ChatsPage />} />
              <Route path="vehiculos" element={<AdminVehicles />} />
              <Route path="visitas" element={<Visits />} />
              <Route path="personal" element={<AuthorizedPersons />} />
              <Route path="elementos" element={<WorkItems />} />
              <Route path="logistica" element={<Logistics />} />
              <Route path="celulares" element={<Phones />} />
              <Route path="guardias" element={<PassiveGuards />} />
              <Route path="cronograma" element={<Schedule />} />
              <Route path="horas-extra" element={<WorkHours />} />
              <Route path="recorridos" element={<SupervisorRoutes />} />
              <Route path="marcaciones" element={<CheckinsLog />} />
              <Route path="cuestionarios" element={<Quizzes />} />
              <Route path="checklist-servicio" element={<ChecklistServicio />} />
              <Route path="manual" element={<Training />} />
              <Route path="marca" element={<BrandManual />} />
              <Route path="alertas" element={<AdminPanic />} />
              <Route path="migracion" element={<Migration />} />
              <Route path="presupuesto" element={<Budget />} />
              <Route path="tareas-operativas" element={<OpsTasksAnalytics />} />
              <Route path="perfil" element={<Profile />} />
            </Route>
            <Route path="/supervisor" element={<ProtectedRoute roles={["supervisor"]}><SupervisorLayout /></ProtectedRoute>}>
              <Route index element={<SupervisorDashboard />} />
              <Route path="inicio-mobile" element={<SupervisorHomeMobile />} />
              <Route path="novedad" element={<SupervisorNewNews />} />
              <Route path="novedades" element={<AdminNews />} />
              <Route path="servicio" element={<SupervisorService />} />
              <Route path="servicios" element={<SupervisorServicesPanel />} />
              <Route path="checkin" element={<SupervisorCheckin />} />
              <Route path="solicitud" element={<SupervisorRequests />} />
              <Route path="solicitudes" element={<AdminRequests />} />
              <Route path="rondas" element={<AdminPatrols />} />
              <Route path="rondas/:id" element={<PatrolDetail />} />
              <Route path="rondas-vivo" element={<LiveTrackingMap />} />
              <Route path="clientes" element={<AdminClients />} />
              <Route path="clientes/:id" element={<ClientDetail />} />
              <Route path="vehiculos" element={<Vehicles />} />
              <Route path="visitas" element={<Visits />} />
              <Route path="personal" element={<AuthorizedPersons />} />
              <Route path="materiales" element={<Materials />} />
              <Route path="elementos" element={<WorkItems />} />
              <Route path="logistica" element={<Logistics />} />
              <Route path="celulares" element={<Phones />} />
              <Route path="guardias" element={<PassiveGuards />} />
              <Route path="cronograma" element={<Schedule />} />
              <Route path="horas-extra" element={<WorkHours />} />
              <Route path="recorridos" element={<SupervisorRoutes />} />
              <Route path="marcaciones" element={<CheckinsLog />} />
              <Route path="cuestionarios" element={<Quizzes />} />
              <Route path="checklist-servicio" element={<ChecklistServicio />} />
              <Route path="manual" element={<Training />} />
              <Route path="marca" element={<BrandManual />} />
              <Route path="chats" element={<ChatsPage />} />
              <Route path="alertas" element={<AdminPanic />} />
              <Route path="tareas-operativas" element={<OpsTasksAnalytics />} />
              <Route path="perfil" element={<Profile />} />
            </Route>

            {/* Vigilador / roles operativos (Custodio, Operario de limpieza) */}
            <Route path="/vigilador" element={<ProtectedRoute roles={OPERATIONAL_ROLES}><MobileLayout /></ProtectedRoute>}>
              <Route index element={<VigiladorHome />} />
              <Route path="ronda/:id" element={<VigiladorPatrolActive />} />
              <Route path="ronda-vista/:id" element={<VigiladorPatrolView />} />
              <Route path="novedad" element={<VigiladorNewNews />} />
              <Route path="solicitud" element={<VigiladorRequests />} />
              <Route path="vehiculos" element={<Vehicles />} />
              <Route path="visitas" element={<Visits />} />
              <Route path="personal" element={<AuthorizedPersons />} />
              <Route path="materiales" element={<Materials />} />
              <Route path="elementos" element={<WorkItems />} />
              <Route path="guardias" element={<PassiveGuards />} />
              <Route path="cronograma" element={<Schedule />} />
              <Route path="manual" element={<Training />} />
              <Route path="chats" element={<ChatsPage />} />
              <Route path="tareas-operativas" element={<VigiladorOpsTasks />} />
              <Route path="perfil" element={<Profile />} />
            </Route>

            {/* Portal del cliente final (solo lectura + alta de autorizados/SUM) */}
            <Route path="/cliente" element={<ProtectedRoute roles={["cliente", "cliente_admin", "inquilino"]}><ClienteLayout /></ProtectedRoute>}>
              <Route index element={<ClientePortal />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <ToastViewport />
          <OfflineBanner />
          <QuizGate />
          <ServiceChecklistGate />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
